/**
 * Deep-link URLs for the admin Explore Chats tab.
 *
 * Admins share these links with other admins to point them straight at a
 * chat or a specific feedback entry. All params are picked up by
 * ``ExploreChats`` on mount.
 */

import { ADMIN_EXPLORE_CHATS_TAB_INDEX } from "@/Admin/tabIndex";
import { Env } from "@/shared/environment/Env";

export interface AdminChatLinkParams {
  sessionId: string;
}

export interface AdminFeedbackLinkParams {
  sessionId: string;
  feedbackId: string;
}

/**
 * Build a fully-qualified URL to ``/admin`` that honours the React Router
 * ``basename`` used on branch-per-URL deploys (e.g. ``/feat-Feedback/admin``).
 *
 * ``window.location.origin`` alone drops the basename, which produces a
 * whitescreen on branch deployments.
 */
function buildAdminUrl(searchParams: URLSearchParams): string {
  const url = new URL(`${Env.appBasePath}/admin`, window.location.origin);
  url.search = searchParams.toString();
  return url.toString();
}

/** URL to open a specific chat inside the admin Chats view. */
export function buildAdminChatLink(params: AdminChatLinkParams): string {
  const searchParams = new URLSearchParams();
  searchParams.set("tab", String(ADMIN_EXPLORE_CHATS_TAB_INDEX));
  searchParams.set("view", "chats");
  searchParams.set("sessionId", params.sessionId);
  return buildAdminUrl(searchParams);
}

/** URL to open a specific feedback entry inside the admin Feedback view. */
export function buildAdminFeedbackLink(
  params: AdminFeedbackLinkParams,
): string {
  const searchParams = new URLSearchParams();
  searchParams.set("tab", String(ADMIN_EXPLORE_CHATS_TAB_INDEX));
  searchParams.set("view", "feedback");
  searchParams.set("sessionId", params.sessionId);
  searchParams.set("feedbackId", params.feedbackId);
  return buildAdminUrl(searchParams);
}
